// Builds the standalone HTML report for "Product Artifact" from
//  - `data`:      the numbers, from the backend (GET /forecast-artifact-data)
//  - `narrative`: the Kaman workflow's output — every string in it is
//                 escaped here, and numbers in it are never used. Shape:
//    {
//      summary:         string,
//      skus:            [{ sku, status, recommendation }],
//      key_issues:      [{ title, detail, severity: "high"|"medium"|"low" }],
//      recommendations: [{ title, detail, priority: "high"|"medium"|"low" }],
//      insights:        [string]
//    }
// The result is a self-contained page (Chart.js from a CDN), meant for an
// iframe srcDoc / download.

import {
  hasWarehouseSplit,
  skippedWarehousesText,
  warehouseSplitLabel,
} from "../../utils/warehouse-split";

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const n0 = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
const n1 = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 });
const usd = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const monthLabel = (ym) => {
  const [y, m] = String(ym).split("-").map(Number);
  if (!y || !m) return String(ym);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
  });
};

const statusClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (/critical|heavily|stock ?out/.test(s)) return "badge-red";
  if (/low|over/.test(s)) return "badge-yellow";
  if (/healthy|good|balanced/.test(s)) return "badge-green";
  return "badge-blue";
};
const levelClass = (level) => {
  const l = String(level || "").toLowerCase();
  return l === "high" ? "rec-high" : l === "low" ? "rec-low" : "rec-med";
};

const safeJson = (obj) => JSON.stringify(obj).replace(/</g, "\\u003c");

const PALETTE = [
  "#2563EB",
  "#7C3AED",
  "#0891B2",
  "#D97706",
  "#DB2777",
  "#EA580C",
  "#4F46E5",
  "#0EA5E9",
  "#DC2626",
  "#C026D3",
];

export const buildArtifactHtml = ({
  data,
  narrative = {},
  darkMode = false,
}) => {
  const skus = data?.skus || [];
  const t = data?.totals || {};
  const w = data?.window || {};
  const bySku = new Map((narrative.skus || []).map((s) => [s.sku, s]));
  const months = (t.monthly_demand || []).map((m) => monthLabel(m.month));
  const fcMonths = (t.monthly_forecast || []).map((m) => monthLabel(m.month));
  const hasForecast = fcMonths.length > 0;

  // SKU labels can be long (e.g. full option names), which crowds a bar
  // chart's x-axis. c2/c3 plot against a serial number instead and rely on
  // `skuLegend` (below) + a tooltip title callback to say which SKU is which.
  const skuIndex = skus.map((_, i) => String(i + 1));

  // Mirrors the app's own dark/light palette (see darkModeContext.js) rather
  // than the OS's prefers-color-scheme — this report should always match
  // whichever theme was active in the app when it was opened.
  const theme = darkMode
    ? {
        bg: "#141824",
        text: "#E2E8F0",
        textSecondary: "#8892A4",
        textMuted: "#6B7280",
        cardBg: "#1B1F2E",
        border: "#2C3045",
        shadow: "0 1px 3px rgba(0,0,0,.4)",
        tableHeadBg: "#242938",
        hoverBg: "#20263A",
        headerGrad1: "#0C0E17",
        headerGrad2: "#05060A",
        insightBg: "#2A2410",
        insightBorder: "#CA9A2E",
        insightStrong: "#EBCB77",
        recText: "#B7C0CE",
        chipBg: "#242938",
        chipText: "#C7D0E0",
        gridLine: "rgba(255,255,255,.08)",
        chartText: "#AEB6C4",
      }
    : {
        bg: "#f0f2f5",
        text: "#1a1a2e",
        textSecondary: "#666",
        textMuted: "#888",
        cardBg: "#fff",
        border: "#e0e0e0",
        shadow: "0 1px 3px rgba(0,0,0,.08)",
        tableHeadBg: "#f8f9fa",
        hoverBg: "#f8f9ff",
        headerGrad1: "#1a1a2e",
        headerGrad2: "#16213e",
        insightBg: "#fff8e1",
        insightBorder: "#ffc107",
        insightStrong: "#856404",
        recText: "#444",
        chipBg: "#f0f2f5",
        chipText: "#333",
        gridLine: "rgba(0,0,0,.08)",
        chartText: "#444",
      };

  // Event uplift the user added on top of the forecast (e.g. Black Friday).
  // Already inside every demand/forecast figure; shown so it isn't read as a trend.
  const upliftTotal = Number(t.event_uplift_units) || 0;
  const upliftEvents = [
    ...new Set(
      skus.flatMap((s) => (s.event_uplift?.events || []).map((e) => e.name)),
    ),
  ];
  // SKUs re-run on their own after the product forecast: the report uses that
  // later run, so its settings (e.g. an event uplift) can differ from the rest.
  const ownRuns = data?.forecast_batch?.skus_from_sku_runs || [];
  const ownRunsNote = ownRuns.length
    ? `<p class="muted" style="margin:10px 0 0;font-size:12.5px">Forecast from a later run of that SKU on its own: ${ownRuns
        .map((r) => `${esc(r.sku)}${r.run_at ? ` (${esc(new Date(r.run_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }))})` : ""}`)
        .join(", ")}. Its settings, such as an event uplift, can differ from the rest of the product.</p>`
    : "";
  const upliftByMonth = (t.monthly_forecast || []).map((m) => {
    const u = skus.reduce(
      (sum, s) =>
        sum +
        (Number(s.event_uplift?.by_month?.find((x) => x.month === m.month)?.units) || 0),
      0,
    );
    return u > 0 ? Math.round(u) : null;
  });

  const chartData = {
    months,
    monthlyDemand: (t.monthly_demand || []).map((m) => m.units),
    labels: skus.map((s) => s.label),
    skuIndex,
    demand: skus.map((s) => s.demand.total),
    inventory: skus.map((s) => s.inventory.on_hand),
    fcMonths,
    forecast: skus.map((s, i) => ({
      label: s.label,
      color: PALETTE[i % PALETTE.length],
      data: (t.monthly_forecast || []).map(
        (m) => s.forecast.find((f) => f.month === m.month)?.units ?? null,
      ),
    })),
    uplift: upliftTotal > 0 ? upliftByMonth : null,
    theme: { text: theme.chartText, grid: theme.gridLine },
  };

  const kpi = (title, value, sub) =>
    `<div class="card"><h3>${esc(title)}</h3><div class="value">${esc(value)}</div><div class="sub">${esc(sub)}</div></div>`;

  const showSplit = hasWarehouseSplit(skus);

  // Warehouse Allocation: split per warehouse + the largest delivery regions.
  const whTotals = t.warehouse_allocation || [];
  const whInfo = data?.warehouse_allocation;
  const allocationHtml =
    whTotals.length && whInfo
      ? `<div class="chart-card"><h3>Warehouse Allocation</h3>
<p class="muted" style="margin:0 0 12px">The forecast was run on all orders, then split by where demand comes from: each delivery region was assigned to the active warehouse with the lowest USPS shipping zone${whInfo.order_window?.months ? `, using orders from the last ${esc(whInfo.order_window.months)} months` : ""}.${
          whInfo.skipped_warehouses?.length
            ? ` Not included: ${esc(skippedWarehousesText(whInfo.skipped_warehouses))}.`
            : ""
        }</p>
<div class="two-col"><div class="scroll-table"><table><thead><tr><th>Warehouse</th><th>Share</th><th>Avg Zone</th><th>Allocated Units</th></tr></thead><tbody>${whTotals
          .map(
            (w) =>
              `<tr><td><strong>${esc(w.name || "Warehouse")}</strong></td><td>${n1(w.pct * 100)}%</td><td>${w.avg_zone == null ? "—" : n1(w.avg_zone)}</td><td>${n0(w.forecast_units)}</td></tr>`,
          )
          .join("")}</tbody></table></div>
<div class="scroll-table"><table><thead><tr><th>Top Delivery Regions</th><th>Order Units</th><th>Nearest Warehouse</th><th>Zone</th></tr></thead><tbody>${(whInfo.zone_mapping || [])
          .map(
            (m) =>
              `<tr><td>${esc(m.zip3)}xx${m.state ? ` · ${esc(m.state)}` : ""}</td><td>${n0(m.units)}</td><td>${esc((m.warehouses || []).join(" / ") || "—")}</td><td>${esc(m.zone ?? "—")}</td></tr>`,
          )
          .join("")}</tbody></table></div></div></div>`
      : "";
  const rows = skus
    .map((s) => {
      const nar = bySku.get(s.sku) || {};
      return `<tr>
        <td><strong>${esc(s.sku)}</strong><div class="muted">${esc(s.label)}</div></td>
        <td>${n0(s.inventory.on_hand)}</td>
        <td>${n0(s.demand.total)}${s.event_uplift?.total_units ? `<div class="muted">incl. +${n0(s.event_uplift.total_units)} event uplift</div>` : ""}</td>
        <td>${n1(s.demand.avg_monthly)}</td>
        <td>${s.coverage_months == null ? "—" : n1(s.coverage_months)}</td>
        <td>${nar.status ? `<span class="badge ${statusClass(nar.status)}">${esc(nar.status)}</span>` : "—"}</td>
        <td>${usd(s.capital_at_cost)}</td>
        ${showSplit ? `<td>${esc(warehouseSplitLabel(s.warehouse_allocation) || "—")}</td>` : ""}
        <td>${esc(nar.recommendation || "")}</td>
      </tr>`;
    })
    .join("");

  const issues = (narrative.key_issues || [])
    .map(
      (i) =>
        `<div class="insight"><strong>${esc(i.title)}</strong> — ${esc(i.detail)}</div>`,
    )
    .join("");
  const recs = (narrative.recommendations || [])
    .map(
      (r) =>
        `<div class="rec-card ${levelClass(r.priority)}"><h4>${esc(r.title)}</h4><p>${esc(r.detail)}</p></div>`,
    )
    .join("");
  const insights = (narrative.insights || [])
    .map((i) => `<li>${esc(i)}</li>`)
    .join("");

  // Maps the serial numbers used on the c2/c3 x-axis back to their SKU.
  const skuLegend = skus
    .map(
      (s, i) =>
        `<span class="sku-chip"><strong>${i + 1}</strong> ${esc(s.label)} <span class="muted">(${esc(s.sku)})</span></span>`,
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${esc(data?.product?.name)} - Product Analysis</title>
<script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"></script>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:${theme.bg};color:${theme.text};padding:24px}
.container{max-width:1400px;margin:0 auto}
.header{background:linear-gradient(135deg,${theme.headerGrad1} 0%,${theme.headerGrad2} 100%);color:#fff;padding:28px 32px;border-radius:16px;margin-bottom:24px}
.header h1{font-size:28px;font-weight:700}
.header p{opacity:.8;margin-top:6px;font-size:14px}
.grid{display:flex;flex-wrap:nowrap;gap:14px;margin-bottom:24px;overflow-x:auto;padding-bottom:4px}
.card{flex:1 1 0;min-width:160px;background:${theme.cardBg};border-radius:12px;padding:16px;box-shadow:${theme.shadow}}
.card h3{font-size:11.5px;text-transform:uppercase;letter-spacing:.5px;color:${theme.textSecondary};margin-bottom:6px;white-space:nowrap}
.card .value{font-size:24px;font-weight:700;white-space:nowrap;color:${theme.text}}
.card .sub{font-size:12px;color:${theme.textSecondary};margin-top:4px}
.chart-card{background:${theme.cardBg};border-radius:12px;padding:20px;box-shadow:${theme.shadow};margin-bottom:16px}
.chart-card h3{font-size:16px;font-weight:600;margin-bottom:16px;color:${theme.text}}
.chart-wrap{position:relative;height:300px}
table{width:100%;border-collapse:collapse;font-size:14px;color:${theme.text}}
th{background:${theme.tableHeadBg};text-align:left;padding:10px 12px;font-weight:600;color:${theme.textSecondary};border-bottom:2px solid ${theme.border}}
td{padding:10px 12px;border-bottom:1px solid ${theme.border};vertical-align:top}
tr:hover td{background:${theme.hoverBg}}
.muted{font-size:11px;color:${theme.textMuted}}
.badge{display:inline-block;padding:2px 10px;border-radius:20px;font-size:12px;font-weight:600}
.badge-green{background:#d4edda;color:#155724}.badge-yellow{background:#fff3cd;color:#856404}
.badge-red{background:#f8d7da;color:#721c24}.badge-blue{background:#d1ecf1;color:#0c5460}
.insight{background:${theme.insightBg};border-left:4px solid ${theme.insightBorder};padding:14px 16px;border-radius:8px;margin:12px 0;font-size:14px;color:${theme.text}}
.insight strong{color:${theme.insightStrong}}
.rec-card{background:${theme.cardBg};border-radius:12px;padding:20px;box-shadow:${theme.shadow};margin-bottom:12px}
.rec-card h4{font-size:15px;margin-bottom:8px;color:${theme.text}}.rec-card p{font-size:14px;color:${theme.recText};line-height:1.5}
.rec-high{border-left:4px solid #dc3545}.rec-med{border-left:4px solid #ffc107}.rec-low{border-left:4px solid #28a745}
.two-col{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.scroll-table{max-height:320px;overflow:auto}
.scroll-table thead th{position:sticky;top:0;z-index:1}
.sku-legend{display:flex;flex-wrap:wrap;gap:8px;margin:-4px 0 16px}
.sku-chip{background:${theme.chipBg};border-radius:6px;padding:3px 9px;font-size:12px;color:${theme.chipText}}
.sku-chip strong{color:${theme.text};margin-right:5px}
h2.sec{font-size:16px;margin:0 0 12px;color:${theme.text}}
ul.ins{padding-left:20px;font-size:14px;line-height:1.7;color:${theme.text}}
@media(max-width:768px){.two-col{grid-template-columns:1fr}}
</style></head><body><div class="container">
<div class="header"><h1>${esc(data?.product?.name)}</h1>
<p>Product analysis &bull; ${esc(w.from)} to ${esc(w.to)} &bull; ${skus.length} SKU${skus.length === 1 ? "" : "s"}</p></div>
<div class="grid">
${kpi("Total Inventory", n0(t.inventory_on_hand), `units on hand across ${skus.length} SKUs`)}
${kpi("Total Demand", n0(t.demand), `units ordered, last ${w.months} months`)}
${kpi("Avg Monthly Demand", n0(t.avg_monthly_demand), "units / month, all SKUs")}
${kpi("Capital in Inventory", usd(t.capital_at_cost), "on-hand units at cost")}
${kpi("Revenue", usd(t.revenue), "demand at list price")}
${kpi("Potential Free Capital", usd(t.potential_free_capital), `by cutting overstock to ${w.target_coverage_months} months of coverage`)}
${upliftTotal > 0 ? kpi("Event Uplift", `+${n0(upliftTotal)}`, `units you added for ${upliftEvents.join(", ")} — included above, not tested`) : ""}
</div>
${narrative.summary || insights ? `<div class="chart-card"><h3>Insight</h3>${narrative.summary ? `<p style="font-size:14px;line-height:1.6;margin-bottom:${insights ? "12px" : "0"}">${esc(narrative.summary)}</p>` : ""}${insights ? `<ul class="ins">${insights}</ul>` : ""}</div>` : ""}
<div class="chart-card"><h3>Monthly Demand (all SKUs)</h3><div class="chart-wrap"><canvas id="c1"></canvas></div></div>
<div class="sku-legend">${skuLegend}</div>
<div class="two-col">
<div class="chart-card"><h3>Demand by SKU</h3><div class="chart-wrap"><canvas id="c2"></canvas></div></div>
<div class="chart-card"><h3>Inventory vs Demand</h3><div class="chart-wrap"><canvas id="c3"></canvas></div></div>
</div>
<div class="chart-card"><h3>SKU Detail &amp; Coverage</h3>
<table><thead><tr><th>SKU</th><th>Inventory</th><th>Total Demand</th><th>Avg Mo. Demand</th><th>Mo. Coverage</th><th>Status</th><th>Capital at Cost</th>${showSplit ? "<th>Warehouse Split</th>" : ""}<th>Recommendation</th></tr></thead><tbody>${rows}</tbody></table>${ownRunsNote}</div>
${hasForecast ? `<div class="chart-card"><h3>Forecast by SKU</h3>${upliftTotal > 0 ? `<p class="muted" style="margin:-8px 0 12px;font-size:12.5px">Includes ${esc(n0(upliftTotal))} units of event uplift you added for ${esc(upliftEvents.join(", "))} (dashed amber line, all SKUs together). It is your own estimate on top of the forecast, not something the forecast found.</p>` : ""}<div class="chart-wrap" style="height:350px"><canvas id="c4"></canvas></div></div>` : ""}
${allocationHtml}
<div class="two-col"><div><h2 class="sec">Key Observations</h2>${issues || '<div class="muted">None reported.</div>'}</div>
<div><h2 class="sec">Recommendations</h2>${recs || '<div class="muted">None reported.</div>'}</div></div>
</div>
<script>
const D=${safeJson(chartData)};
Chart.defaults.color=D.theme.text;
Chart.defaults.borderColor=D.theme.grid;
const bar=(id,labels,datasets,opts={})=>new Chart(document.getElementById(id),{type:'bar',data:{labels,datasets},options:{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:!!opts.legend},tooltip:opts.tooltipLabels?{callbacks:{title:(items)=>opts.tooltipLabels[items[0].dataIndex]}}:{}},scales:{y:{beginAtZero:true,title:{display:true,text:'Units'}},x:{title:opts.xTitle?{display:true,text:opts.xTitle}:undefined}}}});
bar('c1',D.months,[{label:'Units ordered',data:D.monthlyDemand,backgroundColor:'#4a6cf7',borderRadius:4}]);
bar('c2',D.skuIndex,[{label:'Demand',data:D.demand,backgroundColor:'#4a6cf7',borderRadius:4}],{tooltipLabels:D.labels,xTitle:'SKU (see key above)'});
bar('c3',D.skuIndex,[{label:'Inventory',data:D.inventory,backgroundColor:'#4a6cf7',borderRadius:4},{label:'Demand',data:D.demand,backgroundColor:'#ff6b6b',borderRadius:4}],{legend:true,tooltipLabels:D.labels,xTitle:'SKU (see key above)'});
if(document.getElementById('c4'))new Chart(document.getElementById('c4'),{type:'line',data:{labels:D.fcMonths,datasets:D.forecast.map(f=>({label:f.label,data:f.data,borderColor:f.color,tension:.3,fill:false})).concat(D.uplift?[{label:'Event uplift (added, all SKUs)',data:D.uplift,borderColor:'#F59E0B',backgroundColor:'#F59E0B',borderDash:[6,4],tension:0,fill:false,spanGaps:false}]:[])},options:{responsive:true,maintainAspectRatio:false,plugins:{legend:{position:'right'},tooltip:{mode:'index',intersect:false}},scales:{y:{beginAtZero:true,title:{display:true,text:'Forecasted units'}}}}});
</script></body></html>`;
};
